import { PhotoSwipeLightbox } from "vendor";

const DESKTOP_MQ = window.matchMedia("(min-width: 992px)");

class ElMediaZoom {
  constructor(root) {
    if (root.dataset.elMediaZoomReady === "true") return;

    this.root = root;
    this.lightbox = null;
    this.maxZoomLevel = parseFloat(root.getAttribute("allow-zoom")) || 3;
    this.dataSource = this.parseDataSource();
    this.onGestureChanged = this.onGestureChanged.bind(this);

    if (!this.dataSource.length) return;

    root.dataset.elMediaZoomReady = "true";
    this.bindEvents();
  }

  parseDataSource() {
    const dataEl = this.root.querySelector("[data-el-media-zoom-data]");
    if (!dataEl) return [];

    try {
      return JSON.parse(dataEl.textContent);
    } catch (error) {
      console.warn("Invalid el-media-zoom data", error);
      return [];
    }
  }

  get lightboxInstance() {
    if (this.lightbox) return this.lightbox;

    this.lightbox = new PhotoSwipeLightbox({
      pswpModule: () => import("photoswipe"),
      bgOpacity: 1,
      maxZoomLevel: this.maxZoomLevel,
      closeTitle: window.themeVariables?.strings?.closeGallery,
      zoomTitle: window.themeVariables?.strings?.zoomGallery,
      errorMsg: window.themeVariables?.strings?.errorGallery,
      arrowPrev: false,
      arrowNext: false,
      counter: false,
      close: false,
      zoom: false,
    });

    this.lightbox.on("uiRegister", () => this.registerLightboxUi());
    this.lightbox.addFilter("thumbEl", (thumbEl, data) => data.thumbnailElement || thumbEl);
    this.lightbox.init();

    return this.lightbox;
  }

  registerLightboxUi() {
    const pswp = this.lightbox.pswp;

    pswp.ui.registerElement({
      name: "close-button",
      className: "circle-button circle-button--xl hover:animate-icon-block",
      ariaLabel: window.themeVariables?.strings?.closeGallery,
      order: 2,
      isButton: true,
      html: `
        <svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon" viewBox="0 0 16 16">
          <path d="m1 1 14 14M1 15 15 1" stroke="currentColor" stroke-width="1"/>
        </svg>
      `,
      onClick: () => pswp.close(),
    });

    if (pswp.options.dataSource.length <= 1) return;

    pswp.ui.registerElement({
      name: "previous-button",
      className: "circle-button hover:animate-icon-inline",
      ariaLabel: window.themeVariables?.strings?.previous,
      order: 1,
      isButton: true,
      html: `
        <svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon icon--direction-aware" viewBox="0 0 16 18">
          <path d="M11 1 3 9l8 8" stroke="currentColor" stroke-linecap="square"/>
        </svg>
      `,
      onClick: () => pswp.prev(),
    });

    pswp.ui.registerElement({
      name: "next-button",
      className: "circle-button hover:animate-icon-inline",
      ariaLabel: window.themeVariables?.strings?.next,
      order: 3,
      isButton: true,
      html: `
        <svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon icon--direction-aware" viewBox="0 0 16 18">
          <path d="m5 17 8-8-8-8" stroke="currentColor" stroke-linecap="square"/>
        </svg>
      `,
      onClick: () => pswp.next(),
    });
  }

  getCurrentIndex() {
    const swiper = this.root.mainSwiper || this.root.querySelector(".product-media-swiper")?.swiper;
    if (typeof swiper?.realIndex === "number") return swiper.realIndex;
    return 0;
  }

  buildDataSource() {
    const slides = this.root.querySelectorAll(".product-media-swiper .swiper-slide:not(.swiper-slide-duplicate)");

    return this.dataSource.map((item, index) => {
      const slideImage = slides[index]?.querySelector("img");

      return {
        ...item,
        src: item.src,
        srcset: slideImage?.getAttribute("srcset") || undefined,
        msrc: slideImage?.currentSrc || slideImage?.src || item.src,
        thumbnailElement: slideImage || undefined,
      };
    });
  }

  open(index = this.getCurrentIndex()) {
    const dataSource = this.buildDataSource();
    const safeIndex = Math.max(0, Math.min(index, dataSource.length - 1));
    this.lightboxInstance.loadAndOpen(safeIndex, dataSource);
  }

  bindEvents() {
    this.root.addEventListener("click", (event) => {
      const openButton = event.target.closest("[data-el-media-zoom-open]");
      if (openButton) {
        event.preventDefault();
        this.open(this.getCurrentIndex());
        return;
      }

      if (!DESKTOP_MQ.matches || event.target.tagName !== "IMG") return;
      if (!event.target.closest(".product-media-swiper")) return;

      const slide = event.target.closest(".swiper-slide");
      if (!slide) return;

      const indexAttr = slide.getAttribute("data-zoom-index") || slide.getAttribute("data-swiper-slide-index");
      const index = indexAttr != null ? parseInt(indexAttr, 10) : this.getCurrentIndex();
      this.open(Number.isNaN(index) ? this.getCurrentIndex() : index);
    });

    this.root.addEventListener(
      "gesturestart",
      (event) => {
        event.preventDefault();
        this.root.addEventListener("gesturechange", this.onGestureChanged, { passive: false });
      },
      { passive: false }
    );
  }

  onGestureChanged(event) {
    event.preventDefault();
    if (event.scale > 1.5) {
      this.open(this.getCurrentIndex());
      this.root.removeEventListener("gesturechange", this.onGestureChanged);
    }
  }
}

function initElMediaZoom(scope = document) {
  scope.querySelectorAll("[data-el-media-zoom][allow-zoom]").forEach((root) => {
    new ElMediaZoom(root);
  });
}

initElMediaZoom();

document.addEventListener("shopify:section:load", (event) => {
  initElMediaZoom(event.target);
});
